import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { cylinderSearchActions, sliceName } from './store/slices/cylinderSearchSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ObjectTypes, SortDirection } from '../../utils/constants';
import { doSearchStoreActionHandler, loadGridColumnSettingStoreActionHandler, loadSavedFilterStoreActionHandler } from './store/actions/cylinderSearchActions';
import SearchPage from '../../components/SearchPage/SearchPage';
import ColumnActionFormatter, { CheckboxSelectionFormatter } from '../../components/Table/ColumnActionFormatter/ColumnActionFormatter';
import toast from 'react-hot-toast';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Checkbox, Chip, CircularProgress, IconButton, Link, TableCell, Tooltip } from '@mui/material';
import cylinderApi from '../../services/api/CylinderApi';
import { downloadBlobFile } from '../../utils/fileUtils';
import OrderApi from '../../services/api/OrderApi';
import useActionCreation from '../../hooks/useActionCreation';
import FeedbackFormDialog from '../../components/Dialogs/FeedbackFormDialog/FeedbackFormDialog';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { getEndDateWithTime, getStartDateWithTime } from '../../utils/constants/datetime';
import useAuth from '../../hooks/useAuth';
import { PictureAsPdf } from '@mui/icons-material';

const sortColumnList = [
  { value: 'JobName' },
  { value: 'CreatedOn' },
  { value: 'DateReceived' },
];

const statusColors = {
  Scheduled: 'primary',
  Hold: 'secondary',
  'Partially Shipped': 'warning',
  Complete: 'success',
  Cancelled: 'error',
  Returned: 'success',
  Shipped: 'primary',
};

const getSubStatusColor = (status) => {
  const statusColorMap = {
    'At Risk': '#FD8338',
    'At risk': '#FD8338',
    Late: '#00B0F0',
    'On Hand': '#00B050',
    'On Time': '#00B050',
    'Late+': '#B0002D',
  };
  return statusColorMap[status] || 'primary';
};

const CylinderSearch = () => {
  const [openFeedbackComment, setopenFeedbackComment] = useState(false);
  const dispatch = useDispatch();
  const [cylinderId, setCylinderId] = useState();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderNumber = searchParams.get('orderNumber');
  const [cylinderNameFeedback, setCylinderNameFeedback] = useState();
  const [selectedCylinderIDs, setSelectedCylinderIDs] = useState({});
  const [isSelectAllChecked, setIsAllSelected] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const { t } = useTranslation();
  const { dataRowItems, tableInfo, filter } = useSelector((state) => state.cylinderSearch);
  const { timezone } = useAuth();

  const breadcrumbsLinks = useMemo(
    () => [{ to: '/', text: t('Nav_Menu_Home') }, { text: t('Nav_Menu_Cylinders') }],
    [t]
  );
  const onSelectionHandler = useCallback((event, row) => {
    const { OrderIDText } = row;
    const updatedSelectedIds = { ...selectedCylinderIDs, [OrderIDText]: event?.target?.checked };
    setSelectedCylinderIDs(updatedSelectedIds);

    // checking length of all selcted rows
    const selectedItems = Object.keys(updatedSelectedIds)?.length || 0;

    // checking if every row is selected
    const isAllItemsSelected = Object.values(updatedSelectedIds).every((s) => !!s);

    // if every row is selected and row count is equal to datarows count , automatically check the selectall checkbox as true
    if (selectedItems === dataRowItems?.length && isAllItemsSelected) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [dataRowItems?.length, selectedCylinderIDs]);

  const onSelectAllHandler = useCallback((event) => {
    const { checked = false } = event?.target || {};
    setIsAllSelected(checked);
    const updateddataItems = {};
    dataRowItems.forEach((data) => {
      updateddataItems[data?.OrderIDText] = checked;
    });
    setSelectedCylinderIDs(updateddataItems);
  }, [dataRowItems]);

  const handleDownload = async (OrderIDText, FileName, documentType, recordID, CylinderMovementID) => {
    const fileKey = `${OrderIDText}_${recordID}_${CylinderMovementID}`;
    try {
      setDownloadingFiles((prev) => ({ ...prev, [fileKey]: true }));
      const response = await OrderApi.downloadReport(OrderIDText, documentType, recordID);
      if (response) {
        const blob = new Blob([response], { type: 'application/pdf' });
        downloadBlobFile(FileName, blob);
        toast.success(t('Toast_FileDownloadedSuccessfully'));
      }
    } catch (error) {
      toast.error(t('Error_Message_SomethingWentWrong'));
      console.error('Error downloading file:', error);
    } finally {
      setDownloadingFiles((prev) => ({ ...prev, [fileKey]: false }));
    }
  };
  const { buttonLinkBuilder, actionDialogComponent } = useActionCreation({
    dialogTitleKey: 'Dialog_Title_NewActionForCylinder',
    belongsTo: 'Cylinders',
    sourceLinkCallback: ({ id }) => `/Orders/${id}/view`,
  });

  const addFeedback = useCallback(({ CylinderID, CylinderMovementID }) => () => {
    setopenFeedbackComment(true);
    setCylinderId(String(CylinderMovementID));
    setCylinderNameFeedback(CylinderID);
  }, []);

  const closeFeedbackDialogHandler = () => {
    setopenFeedbackComment(false);
  };
  useEffect(() => {
    if (orderNumber) {
      const cylinderFilter = [
        {
          Fields: [
            { Field: 'clientIDs', Value: [] },
            { Field: 'OrderNumber', Value: orderNumber },
          ]
        }
      ];
      const { Fields } = cylinderFilter[0];
      dispatch(cylinderSearchActions.applySavedFilter(Fields));
      dispatch(doSearchStoreActionHandler());
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('orderNumber');
      setSearchParams(newParams, { replace: true });
    }
  }, [dispatch, orderNumber, searchParams, setSearchParams]);
  const cellMetaData = [
    {
      icon: <Tooltip title="Add Feedback"><FeedbackOutlinedIcon fontSize="small" /></Tooltip>,
      handler: addFeedback
    }
  ];
  const columnMappingFormatter = {
    Action: ({ row }) => (
      <>
        <ColumnActionFormatter cellMetaData={cellMetaData} rowItem={row} />
        {buttonLinkBuilder({
          id: String(row?.CylinderMovementID),
          text: row?.CylinderMovementID,
        })}
      </>
    ),
    Selection: ({ row }) => (
      <CheckboxSelectionFormatter
        isSelectAllChecked={isSelectAllChecked}
        objectId={row?.OrderIDText}
        selectedObjectIDs={selectedCylinderIDs}
        rowItem={row}
        handler={onSelectionHandler}
      />
    ),
    StatusName: ({ row, field }) => {
      const statusValue = row?.[field];
      if (!statusValue) return '';
      const labelColor = statusColors[statusValue];
      return (
        <Chip
          label={statusValue}
          color={labelColor}
          sx={{ minWidth: 90 }}
          size="small"
          variant="filled"
        />
      );
    },
    SubStatusName: ({ row, field }) => {
      const statusValue = row?.[field];
      if (!statusValue) return '';
      const labelColor = getSubStatusColor(statusValue);
      return (
        <Chip
          label={statusValue}
          sx={{
            minWidth: 90,
            backgroundColor: labelColor,
            color: (theme) => (theme.palette.mode === 'dark' ? '#000' : '#fff'),
          }}
          size="small"
          variant="filled"
        />
      );
    },
    OrderNumber: ({ row, field }) => (
      <Link
        color="primary"
        component={RouterLink}
        to={`/Orders/${row.OrderIDText}/view`}
        underline="none"
        variant="subtitle2"
      >
        {row[field]}
      </Link>),
    // DeliveryDate: ({ row }) => (row?.DeliveryDate ? formatDateTime(row.DeliveryDate, dateFormat) : ''),
    // LabReceivedDate: ({ row }) => (row?.LabReceivedDate ? formatDateTime(row.LabReceivedDate, dateFormat) : ''),
    // ReturnDueDate: ({ row }) => (row?.ReturnDueDate ? formatDateTime(row.ReturnDueDate, dateFormat) : ''),
    // ReturnGracePeriodDate: ({ row }) => (row?.ReturnGracePeriodDate ? formatDateTime(row.ReturnGracePeriodDate, dateFormat) : ''),
    // ShippedDate: ({ row }) => (row?.ShippedDate ? formatDateTime(row.ShippedDate, dateFormat) : ''),
    Waybill: ({ row, field }) => {
      const waybill = row?.[field];
      return waybill &&
        row?.TrackingURL ? (
          <Link
            href={row?.TrackingURL && !/^https?:\/\//i.test(row.TrackingURL) ? `https://${row.TrackingURL}` : row?.TrackingURL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ cursor: 'pointer' }}
          >
            {waybill}
          </Link>
        ) : (
          waybill || ''
        );
    },
    AttachedFiles: ({ row }) => (row?.AttachedFiles?.length > 0 ? (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {row?.AttachedFiles?.map((file) => {
          const fileKey = `${row.OrderIDText}_${file?.RecordID}_${row?.CylinderMovementID}`;
          const isDownloading = downloadingFiles[fileKey];
          return (
            <Tooltip title={file.FileName} key={fileKey}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDownload(row.OrderIDText, file.FileName, file.DocumentType, file?.RecordID, row?.CylinderMovementID)}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <PictureAsPdf />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
      </Box>
    ) : null)

  };
  const columnHeaderMappingFormatter = useMemo(() => ({ key: 'Selection',
    renderer: (
      <TableCell
        key="tableCol-Selection"
        sx={{
          whiteSpace: 'nowrap',
        }}
      >
        <Checkbox
          sx={{
            '& .MuiSvgIcon-root': { fontSize: 24 },
            padding: 0,
          }}
          onChange={onSelectAllHandler}
          checked={isSelectAllChecked}
        />
      </TableCell>
    ) }), [isSelectAllChecked, onSelectAllHandler]);

  const getExportingDataHandler = useCallback(
    async (newPageSize) => {
      try {
        const searchParamData = {
          clientIDs: filter?.clientIDs,
          cylinderType: filter?.CylinderType?.id ?? null,
          cylinderStatus: filter?.Status?.id ?? null,
          orderNumber: filter?.OrderNumber ?? '',
          rentalAgreementNumber: filter?.RentalAgreementNumber ?? '',
          cylinderSubStatus: filter?.SubStatus?.id ?? null,
          requestor: filter?.Requestor?.id ?? '',
          reportAddressIDs: filter?.reportAddressIDs,
          daysOut: filter?.DaysOut ?? null,
          shippingDateFrom: filter?.ShippedDateFrom ? getStartDateWithTime(filter?.ShippedDateFrom, timezone).toISOString() : null,
          shippingDateTo: filter?.ShippedDateTo ? getEndDateWithTime(filter?.ShippedDateTo, timezone).toISOString() : null,
          functionality: filter?.functionality ?? 'Cylinder',
          scenarioIDs: filter?.scenarioIDs,
          returnDueDateFrom: filter?.ReturnDueDateFrom ? getStartDateWithTime(filter?.ReturnDueDateFrom, timezone).toISOString() : null,
          returnDueDateTo: filter?.ReturnDueDateTo ? getEndDateWithTime(filter?.ReturnDueDateTo, timezone).toISOString() : null,

          page: tableInfo.page + 1,
          pageSize: newPageSize,
          sortBy: SortDirection.combineSortBy(
            tableInfo.sortColumn,
            tableInfo.sortDirection
          ),
        };

        const data = await cylinderApi.searchCylinder(searchParamData);
        return data.Data;
      } catch (error) {
        toast.error('Something went wrong');
      }
      return [];
    },
    [filter?.clientIDs, filter?.CylinderType?.id, filter?.Status?.id, filter?.OrderNumber, filter?.RentalAgreementNumber, filter?.SubStatus?.id, filter?.Requestor?.id, filter?.reportAddressIDs, filter?.DaysOut, filter?.ShippedDateFrom, filter?.ShippedDateTo, filter?.functionality, filter?.scenarioIDs, filter?.ReturnDueDateFrom, filter?.ReturnDueDateTo, timezone, tableInfo.page, tableInfo.sortColumn, tableInfo.sortDirection]
  );

  return (
    <>
      <SearchPage
        pageTitle={`${t('Tab_Title_AppName')} | ${t('Header_CylinderSearch')}`}
        headerText={t('Header_CylinderSearch')}
        breadcrumbsLinks={breadcrumbsLinks}
        sortColumnList={sortColumnList}
        topRightSectionComponent={null}
        filterSettingObjectType={ObjectTypes.SearchFilter_Cylinders}
        savedFilterObjectType={ObjectTypes.CylinderSearchUserFilter}
        reduxSliceName={sliceName}
        reduxSearchActions={cylinderSearchActions}
        doSearchStoreActionHandler={doSearchStoreActionHandler}
        loadGridColumnSettingStoreActionHandler={loadGridColumnSettingStoreActionHandler}
        loadSavedFilterStoreActionHandler={loadSavedFilterStoreActionHandler}
        getExportingDataHandler={getExportingDataHandler}
        columnSettingObjectType={ObjectTypes.CylinderSearchGrid}
        columnMappingFormatter={columnMappingFormatter}
        exportFilename="Cylinder_List_Exported"
        columnHeaderMappingFormatter={columnHeaderMappingFormatter}
      />
      <FeedbackFormDialog
        isOpen={openFeedbackComment}
        closeDialog={closeFeedbackDialogHandler}
        recordId={cylinderId}
        objectTitle={cylinderNameFeedback}
        belongsTo="CylinderMovement"
      />
      {actionDialogComponent}
    </>
  );
};

export default CylinderSearch;
